import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { isAxiosError } from 'axios';
import { listEmailHistory, sendEmail } from '../../../api/email';
import { listLookup } from '../../../api/masterData';
import { listAttachments } from '../../../api/leads';

export const EmailTab = ({ leadId, hasEmail }: { leadId: number; hasEmail: boolean }) => {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const historyQuery = useQuery({ queryKey: ['email-history', leadId], queryFn: () => listEmailHistory(leadId) });
  const templatesQuery = useQuery({
    queryKey: ['lookup', '/master-data/email-templates'],
    queryFn: () => listLookup('/master-data/email-templates', { activeOnly: true }),
  });
  const attachmentsQuery = useQuery({ queryKey: ['attachments', leadId], queryFn: () => listAttachments(leadId) });

  const sendMutation = useMutation({
    mutationFn: () =>
      sendEmail({
        leadId,
        templateId: templateId || undefined,
        subject: templateId ? undefined : subject,
        body: templateId ? undefined : body,
        attachmentIds: selectedAttachmentIds.length ? selectedAttachmentIds : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-history', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead-timeline', leadId] });
      setSubject('');
      setBody('');
      setTemplateId('');
      setSelectedAttachmentIds([]);
      setError(null);
    },
    onError: (err) =>
      setError(isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to send') : 'Failed to send'),
  });

  if (!hasEmail) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        This lead has no email address on file.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Send an email
        </Typography>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="Use a template (optional)"
            size="small"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>Write manually</em>
            </MenuItem>
            {templatesQuery.data?.map((t) => (
              <MenuItem key={t.id as number} value={t.id as number}>
                {String(t.name)}
              </MenuItem>
            ))}
          </TextField>
          {!templateId && (
            <>
              <TextField label="Subject" fullWidth value={subject} onChange={(e) => setSubject(e.target.value)} />
              <TextField
                label="Body (HTML)"
                multiline
                minRows={3}
                fullWidth
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </>
          )}
          {(attachmentsQuery.data?.length ?? 0) > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Attach existing files:
              </Typography>
              {attachmentsQuery.data?.map((a) => (
                <FormControlLabel
                  key={a.id}
                  sx={{ display: 'block' }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedAttachmentIds.includes(a.id)}
                      onChange={(e) =>
                        setSelectedAttachmentIds((prev) =>
                          e.target.checked ? [...prev, a.id] : prev.filter((id) => id !== a.id),
                        )
                      }
                    />
                  }
                  label={a.originalName}
                />
              ))}
            </Box>
          )}
          <Button
            variant="contained"
            sx={{ alignSelf: 'flex-start' }}
            disabled={(!templateId && (!subject.trim() || !body.trim())) || sendMutation.isPending}
            onClick={() => sendMutation.mutate()}
          >
            {sendMutation.isPending ? 'Sending…' : 'Send'}
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={1.5}>
        {historyQuery.data?.map((m) => (
          <Paper key={m.id} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2">{m.subject}</Typography>
            {/* Rendered as plain text, not dangerouslySetInnerHTML: body can
                contain arbitrary HTML supplied by any user with email:send
                permission, and this view is seen by other staff members. */}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {m.body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {m.status} · {new Date(m.createdAt).toLocaleString()}
            </Typography>
          </Paper>
        ))}
        {historyQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No emails sent yet.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
